import React from 'react'
import { useDispatch } from 'react-redux'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiClipboard } from '@react-icons/all-files/fi/FiClipboard'
import { FiClock } from '@react-icons/all-files/fi/FiClock'
import { FiLayers } from '@react-icons/all-files/fi/FiLayers'
import { FiMail } from '@react-icons/all-files/fi/FiMail'
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiSearch } from '@react-icons/all-files/fi/FiSearch'
import { FiSun } from '@react-icons/all-files/fi/FiSun'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { FiVolume2 } from '@react-icons/all-files/fi/FiVolume2'
import { SHOW_POPUP } from '../../../store/slices/ui'
import * as gtag from '../../../lib/gtag'
import ProductionPopup from '../../DeferredProductionPopup'
import { useOrderFlow } from '../../unified-order/OrderFlowProvider'
import PageShell from '../foundations/PageShell'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import ServiceHero from './ServiceHero'
import ReviewProof from './ReviewProof'
import FAQGroup from './FAQGroup'
import CorporateProposalForm from './CorporateProposalForm'
import styles from './CorporateProductionPage.module.scss'

const capabilityIcons = {
  audio: FiVolume2,
  video: FiMonitor,
  lighting: FiSun,
  staging: FiLayers,
  crew: FiUsers,
  advance: FiClipboard
}

const eventTypeIcons = [FiVolume2, FiUsers, FiMonitor, FiSun, FiLayers]

const SectionHeading = ({ eyebrow, title, description, inverse = false, id }) => (
  <div className={`${styles.sectionHeading} ${inverse ? styles.inverseHeading : ''}`.trim()}>
    {eyebrow && <span>{eyebrow}</span>}
    <h2 id={id}>{title}</h2>
    {description && <p>{description}</p>}
  </div>
)

const ConcertProductionPage = ({ content, reviewData }) => {
  const dispatch = useDispatch()
  const { launchIntent } = useOrderFlow()

  const track = (action, value = 3, sourceSurface) => gtag.event({
    action,
    label: 'leads',
    value,
    category: 'orders',
    sourceSurface
  })

  const openProposal = sourceSurface => {
    track('concert_production_proposal_open', 15, sourceSurface)
    launchIntent({ sourceSurface, fulfillment: 'delivery_setup' }, () => {
      dispatch(SHOW_POPUP({
        key: 'concert-production-proposal',
        size: 'big',
        trapFocus: true,
        ariaLabelledBy: 'concert-proposal-modal-title',
        body: (
          <PageShell className={styles.modalShell}>
            <div className={styles.modalForm}>
              <span>Concert production</span>
              <h2 id='concert-proposal-modal-title' tabIndex={-1} data-dialog-initial-focus>Tell us about the show</h2>
              <p>Share the details and rider you already have. A producer will help define the technical scope and next step.</p>
              <CorporateProposalForm idPrefix='concert-proposal-modal' mode='concert' compact />
            </div>
          </PageShell>
        )
      }))
    })
  }

  const openBuilder = sourceSurface => {
    track('concert_production_builder_open', 3, sourceSurface)
    launchIntent({ sourceSurface, fulfillment: 'delivery_setup' }, () => {
      dispatch(SHOW_POPUP({ size: 'xl', key: 'prodpop', body: <ProductionPopup /> }))
    })
  }

  const rating = reviewData?.source?.averageRating
  const reviewCount = reviewData?.source?.totalReviewCount
  const proof = content.proof.map(item => {
    if (item.key === 'rating' && rating) return { ...item, value: `${rating} / 5` }
    if (item.key === 'reviews' && reviewCount) return { ...item, value: Number(reviewCount).toLocaleString('en-US') }
    return item
  })

  const processSteps = content.process.steps.map((step, index) => ({
    title: step.title,
    description: step.description,
    icon: [FiSearch, FiClipboard, FiClock, FiCheck][index]
  }))

  return (
    <PageShell className={styles.page}>
      <ServiceHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Concert Production' }
        ]}
        eyebrow={content.hero.eyebrow}
        title={content.hero.title}
        description={content.hero.description}
        image={content.hero.image}
        imageAlt={content.hero.imageAlt}
        stackActionsOnMobile
        primaryAction={{
          label: content.hero.primaryAction,
          icon: FiMail,
          onClick: () => openProposal('concert_production_hero'),
          channel: 'quote',
          conversionKey: 'concert-production.hero.proposal',
          salesPath: 'managed'
        }}
        secondaryAction={{
          label: content.hero.secondaryAction,
          icon: FiPhone,
          href: 'tel:+13059744411',
          external: true,
          onClick: () => track('concert_production_hero_call', 15, 'concert_production_hero'),
          channel: 'phone',
          conversionKey: 'concert-production.hero.call',
          salesPath: 'managed'
        }}
        processLabel='Concert production process'
        steps={processSteps}
      />

      <section className={styles.proof} aria-label='Concert production proof'>
        <Container className={styles.proofGrid}>
          {proof.map(item => (
            <div key={item.key}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </Container>
      </section>

      <section className={styles.eventTypes} aria-labelledby='concert-event-types-title'>
        <Container>
          <SectionHeading id='concert-event-types-title' {...content.eventTypes} />
          <div className={styles.eventTypeGrid}>
            {content.eventTypes.items.map((item, index) => {
              const Icon = eventTypeIcons[index % eventTypeIcons.length]
              return (
                <article key={item.title}>
                  <Icon aria-hidden='true' />
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <section className={styles.capabilities} aria-labelledby='concert-capabilities-title'>
        <Container>
          <SectionHeading id='concert-capabilities-title' inverse {...content.capabilities} />
          <div className={styles.capabilityGrid}>
            {content.capabilities.items.map(item => {
              const Icon = capabilityIcons[item.icon] || FiCheck
              return (
                <article key={item.title}>
                  <span><Icon aria-hidden='true' /></span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <section className={styles.caseStudies} aria-labelledby='concert-case-studies-title'>
        <Container>
          <SectionHeading
            id='concert-case-studies-title'
            eyebrow='Documented live production work'
            title='Real concerts and performance productions in South Florida'
            description='Review authentic project photography, production context, crew size, and technical scope—not stock examples or generic claims.'
          />
          <div className={styles.caseGrid}>
            {content.caseStudies.map(project => (
              <article className={styles.caseCard} key={project.slug}>
                <Media src={project.image} alt={project.imageAlt} ratio='4 / 3' policy={MEDIA_POLICY_KEYS.CARD} />
                <div className={styles.caseCopy}>
                  <span>{project.type}</span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <ul aria-label={`${project.title} production facts`}>
                    {project.facts.map(fact => <li key={fact}>{fact}</li>)}
                  </ul>
                  <Action
                    variant='plain'
                    href={`/portfolio/${project.slug}`}
                    icon={FiArrowRight}
                    dataConversionChannel='other'
                    dataConversionKey='concert-production.case-study'
                    dataConversionLocation='case-studies'
                    dataConversionSalesPath='managed'
                    onClick={() => track('concert_production_case_study', 2, project.slug)}
                  >
                    View documented project
                  </Action>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className={styles.processSection} aria-labelledby='concert-process-title'>
        <Container className={styles.splitLayout}>
          <div className={styles.splitMedia}>
            <Media src={content.process.image} alt={content.process.imageAlt} ratio='4 / 5' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} />
          </div>
          <div className={styles.splitCopy}>
            <SectionHeading id='concert-process-title' eyebrow={content.process.eyebrow} title={content.process.title} description={content.process.description} />
            <ol className={styles.processList}>
              {content.process.steps.map((step, index) => (
                <li key={step.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Container>
      </section>

      <section className={styles.venue} aria-labelledby='concert-logistics-title'>
        <Container className={styles.venueLayout}>
          <div className={styles.venueCopy}>
            <SectionHeading id='concert-logistics-title' eyebrow={content.logistics.eyebrow} title={content.logistics.title} description={content.logistics.description} />
            <ul>
              {content.logistics.items.map(item => <li key={item}><FiCheck aria-hidden='true' /><span>{item}</span></li>)}
            </ul>
            <div className={styles.venueActions}>
              <Action onClick={() => openProposal('concert_production_logistics')} icon={FiArrowRight}>Discuss your venue and rider</Action>
              <Action variant='plain' href='/portfolio' icon={FiArrowRight}>View production portfolio</Action>
            </div>
          </div>
          <Media src={content.logistics.image} alt={content.logistics.imageAlt} ratio='5 / 4' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} />
        </Container>
      </section>

      <ReviewProof
        eyebrow='Verified Google feedback'
        heading='Feedback connected to concerts, stages, lighting, and live events'
        pagePath='/concert-production'
        initialData={reviewData}
        sectionId='concert-production-reviews'
      />

      <FAQGroup
        questions={content.faqs}
        eyebrow='Concert planning questions'
        title='Concert production FAQs'
        intro='Clear answers about technical riders, venues, festivals, outdoor shows, timelines, pricing, and service coverage.'
        headingId='concert-production-faqs'
      />

      <section className={styles.proposal} aria-labelledby='concert-proposal-title'>
        <Container className={styles.proposalLayout}>
          <div className={styles.proposalIntro}>
            <SectionHeading id='concert-proposal-title' eyebrow={content.proposal.eyebrow} title={content.proposal.title} description={content.proposal.description} />
            <div className={styles.proposalNotes}>
              <span><FiClock aria-hidden='true' /> Share what you know now; technical details can be advanced later.</span>
              <span><FiMapPin aria-hidden='true' /> Miami, Fort Lauderdale, and South Florida.</span>
              <span><FiUsers aria-hidden='true' /> Reviewed by a production team, not a generic rental queue.</span>
            </div>
          </div>
          <div className={styles.proposalFormCard}>
            <CorporateProposalForm idPrefix='concert-proposal-inline' mode='concert' />
          </div>
        </Container>
      </section>

      <section className={styles.builderBand} aria-labelledby='concert-builder-title'>
        <Container className={styles.builderLayout}>
          <div>
            <span>{content.builder.eyebrow}</span>
            <h2 id='concert-builder-title'>{content.builder.title}</h2>
            <p>{content.builder.description}</p>
          </div>
          <Action
            variant='secondary'
            size='large'
            icon={FiCalendar}
            onClick={() => openBuilder('concert_production_late_builder')}
            dataConversionChannel='builder'
            dataConversionKey='concert-production.late-builder'
            dataConversionLocation='late-builder'
            dataConversionSalesPath='self_service'
          >
            {content.builder.action}
          </Action>
        </Container>
      </section>

      <section className={styles.related} aria-labelledby='concert-related-title'>
        <Container>
          <SectionHeading id='concert-related-title' eyebrow={content.related.eyebrow} title={content.related.title} />
          <div className={styles.relatedGrid}>
            {content.related.items.map(item => (
              <article key={item.href}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <Action variant='plain' href={item.href} icon={FiArrowRight}>Open {item.title}</Action>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </PageShell>
  )
}

export default ConcertProductionPage
